// SPDX-FileCopyrightText: 2025 TRUMPF Laser SE
//
// SPDX-License-Identifier: LicenseRef-TRUMPF
import { IdWithValue } from 'hooks/useModelEvaluationApi';
import useProjectsMetadata from 'hooks/useProjectsMetadata';
import ParamsWithId from 'model/ParamsWithId';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';
import { OutputText } from '@tls/treact-ui';

export type Props = {
  evaluation: IdWithValue[];
};

export default function MeanTable({ evaluation }: Readonly<Props>) {
  const { id } = useParams<ParamsWithId>();
  const { selectedProject } = useProjectsMetadata(id);

  const { t } = useTranslation();

  const modelClassesWithoutBackground = useMemo(
    () => selectedProject?.projectUseCase?.modelClasses?.filter(modelClass => modelClass.classIndex !== 0),
    [selectedProject?.projectUseCase?.modelClasses]
  );
  const means = useMemo(() => {
    const data: number[][] = [];

    if (evaluation.length > 0) {
      // Initialize the data array with empty arrays for each evaluation
      // This is necessary to avoid undefined values in the data array
      evaluation[0].value.forEach(() => {
        data.push([]);
      });
    }

    evaluation.forEach(e => {
      e.value.forEach((value, index) => {
        data[index].push(value);
      });
    });

    return data.map(d => calculateMean(d));
  }, [evaluation]);

  return (
    <Root>
      <Title>{t('project.evaluation.mean')}</Title>
      <Table>
        {means.map((mean, index) => {
          const modelClass = modelClassesWithoutBackground?.find(mc => mc.classIndex === index + 1);
          return (
            <TableCell key={modelClass?.className}>
              <StyledOutputText asAtom infoSecondary={mean.toFixed(2)} label={modelClass?.className ?? ''} />
            </TableCell>
          );
        })}
      </Table>
    </Root>
  );
}

const Root = styled.div`
  height: 100%;
  width: 100%;
  display: flex;
  border-top: 1px solid #ccc;
`;

const Title = styled.div`
  width: 62px;
  align-self: center;
  text-align: center;
  font-weight: bold;
`;
const Table = styled.div`
  display: flex;
  flex: 1;
  padding-right: 12px;
`;
const TableCell = styled.div`
  width: 0px;
  flex-grow: 1;
  padding: 0 0.5rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  border-left: 1px solid #ccc;

  &:is(:last-child) {
    border-right: 1px solid #ccc;
  }
`;

const StyledOutputText = styled(OutputText)`
  max-width: 200px;
  span {
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
  }
`;

export function calculateMean(numbers: number[]) {
  const total = numbers.reduce((acc, val) => acc + val, 0);
  return total / numbers.length;
}
